import { create } from 'zustand';
import { queryRecords, createRecord, updateRecord, deleteRecord, fetchRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';
import { useAuthStore } from '../auth/auth-store';
import type { Assessment, AssessmentPhoto } from '../types/impact';
export type { Assessment, AssessmentPhoto } from '../types/impact';
import type { QueryOptions } from '../api/types';

const F = FIELDS.impactAssessments;
const FP = FIELDS.impactPhotos;

function mapAssessment(r: Record<string, unknown>): Assessment {
  return {
    id: r.record_id as number, incidentId: Number(r[F.incidentId]) || null,
    assessmentType: (r[F.assessmentType] as string) || '', address: (r[F.address] as string) || '',
    city: (r[F.city] as string) || '', state: (r[F.state] as string) || '',
    county: (r[F.county] as string) || '', zip: (r[F.zip] as string) || '',
    latitude: r[F.latitude] != null ? Number(r[F.latitude]) : null,
    longitude: r[F.longitude] != null ? Number(r[F.longitude]) : null,
    damageCategory: (r[F.damageCategory] as string) || '', description: (r[F.description] as string) || '',
    assessorName: (r[F.assessorName] as string) || '', assessorEmail: (r[F.assessorEmail] as string) || '',
    assessmentDate: (r[F.assessmentDate] as string) || '', estimatedLoss: Number(r[F.estimatedLoss]) || 0,
    occupancyType: (r[F.occupancyType] as string) || '', structureType: (r[F.structureType] as string) || '',
    stories: Number(r[F.stories]) || 0, floodDepth: Number(r[F.floodDepth]) || 0,
    status: (r[F.status] as string) || 'draft', notes: (r[F.notes] as string) || '',
    datecreated: (r.created_at as string) || '',
    homeCount: Number((r as Record<string, unknown>)['homeCount']) || 1,
    severity: (r as Record<string, unknown>)['severity'] != null ? Number((r as Record<string, unknown>)['severity']) : null,
    assessorPersonId: F.assessorPersonId ? (r[F.assessorPersonId] as string) || '' : '',
    paProjectId: F.paProjectId ? (r[F.paProjectId] as string) || '' : '',
  };
}

function mapPhoto(r: Record<string, unknown>): AssessmentPhoto {
  return {
    id: r.record_id as number,
    assessmentId: Number(r[FP.assessmentId]) || 0,
    photoUrl: (r[FP.photoData] as string) || '',
    caption: (r[FP.caption] as string) || '',
    category: (r[FP.category] as string) || '',
    latitude: Number(r[FP.latitude]) || null,
    longitude: Number(r[FP.longitude]) || null,
    takenBy: (r[FP.takenBy] as string) || '',
    takenAt: (r[FP.takenAt] as string) || '',
  };
}

function toPayload(data: Partial<Assessment>): Record<string, unknown> {
  const p: Record<string, unknown> = {};
  if (data.incidentId !== undefined) p[F.incidentId] = data.incidentId;
  if (data.assessmentType !== undefined) p[F.assessmentType] = data.assessmentType;
  if (data.address !== undefined) p[F.address] = data.address;
  if (data.city !== undefined) p[F.city] = data.city;
  if (data.state !== undefined) p[F.state] = data.state;
  if (data.county !== undefined) p[F.county] = data.county;
  if (data.zip !== undefined) p[F.zip] = data.zip;
  if (data.damageCategory !== undefined) p[F.damageCategory] = data.damageCategory;
  if (data.description !== undefined) p[F.description] = data.description;
  if (data.assessorName !== undefined) p[F.assessorName] = data.assessorName;
  if (data.assessorEmail !== undefined) p[F.assessorEmail] = data.assessorEmail;
  if (data.assessmentDate !== undefined) p[F.assessmentDate] = data.assessmentDate;
  if (data.estimatedLoss !== undefined) p[F.estimatedLoss] = data.estimatedLoss;
  if (data.occupancyType !== undefined) p[F.occupancyType] = data.occupancyType;
  if (data.structureType !== undefined) p[F.structureType] = data.structureType;
  if (data.stories !== undefined) p[F.stories] = data.stories;
  if (data.floodDepth !== undefined) p[F.floodDepth] = data.floodDepth;
  if (data.status !== undefined) p[F.status] = data.status;
  if (data.latitude !== undefined) p[F.latitude] = data.latitude;
  if (data.longitude !== undefined) p[F.longitude] = data.longitude;
  if (data.notes !== undefined) p[F.notes] = data.notes;
  if (F.assessorPersonId && (data as any).assessorPersonId !== undefined) p[F.assessorPersonId] = String((data as any).assessorPersonId);
  if (F.paProjectId && (data as any).paProjectId !== undefined) p[F.paProjectId] = String((data as any).paProjectId);
  return p;
}

interface ImpactState {
  assessments: Assessment[]; total: number; loading: boolean; page: number;
  fetch: (opts?: Partial<QueryOptions>) => Promise<void>;
  fetchAll: () => Promise<void>;
  fetchById: (id: number) => Promise<Assessment | null>;
  setPage: (p: number) => void;
  create: (data: Partial<Assessment>) => Promise<number | null>;
  update: (id: number, data: Partial<Assessment>) => Promise<void>;
  remove: (id: number) => Promise<void>;
  bulkCreate: (items: Partial<Assessment>[]) => Promise<number[]>;
  // Photos
  loadPhotos: (assessmentId: number) => Promise<AssessmentPhoto[]>;
  uploadPhoto: (assessmentId: number, base64: string, index: number, meta?: { caption?: string; takenBy?: string }) => Promise<void>;

  // V1 compat aliases
  isLoading: boolean;
  loadAssessments: () => Promise<void>;
  createAssessment: (data: any) => Promise<number | null>;
  updateAssessment: (id: any, data: any) => Promise<void>;
  deleteAssessment: (id: any) => Promise<void>;
  bulkCreateAssessments: (items: any[]) => Promise<number[]>;
  createSelfReport: (contactData: any, damageData?: any) => Promise<number | null>;
}

export const useImpactStore = create<ImpactState>((set, get) => ({
  assessments: [], total: 0, loading: false, page: 1,

  fetch: async (opts) => {
    set({ loading: true });
    try {
      const { page } = get();
      const inc = useAuthStore.getState().activeIncident;
      const incFilter = inc ? [{ field: F.incidentId, operator: 'eq' as const, value: inc.id }] : [];
      const existingFilters = opts?.filters || [];
      const res = await queryRecords<Record<string, unknown>>(TABLES.impactAssessments, {
        limit: 50, offset: (page - 1) * 50, sort: { field: 'created_at', direction: 'desc' }, ...opts,
        filters: [...incFilter, ...existingFilters],
      });
      set({ assessments: res.records.map(mapAssessment), total: res.total });
    } catch (e) { console.error('[Impact] fetch', e); }
    set({ loading: false });
  },

  fetchAll: async () => {
    set({ loading: true });
    try {
      const inc = useAuthStore.getState().activeIncident;
      const filters = inc ? [{ field: F.incidentId, operator: 'eq' as const, value: inc.id }] : [];
      const res = await queryRecords<Record<string, unknown>>(TABLES.impactAssessments, {
        limit: 2000, sort: { field: 'created_at', direction: 'desc' }, filters,
      });
      set({ assessments: res.records.map(mapAssessment), total: res.total });
    } catch (e) { console.error('[Impact] fetchAll', e); }
    set({ loading: false });
  },

  fetchById: async (id: number) => {
    try {
      const r = await fetchRecord<Record<string, unknown>>(TABLES.impactAssessments, id);
      if (!r) return null;
      const mapped = mapAssessment(r);
      // Merge into store so all components see the fresh record
      set(state => ({
        assessments: state.assessments.some(a => a.id === id)
          ? state.assessments.map(a => a.id === id ? mapped : a)
          : [...state.assessments, mapped],
      }));
      return mapped;
    } catch (e) { console.error('[Impact] fetchById', e); return null; }
  },

  setPage: (p) => { set({ page: p }); get().fetch(); },

  create: async (data) => {
    try {
      // Auto-resolve assessorPersonId from the logged-in user's email
      const enriched: any = { ...data };
      if (F.assessorPersonId && !enriched.assessorPersonId && TABLES.people && FIELDS.people?.email) {
        const userEmail = useAuthStore.getState().user?.email;
        if (userEmail) {
          try {
            const pr = await queryRecords(TABLES.people, {
              filters: [{ field: FIELDS.people.email, operator: 'eq', value: userEmail }],
              limit: 1,
            });
            if (pr.records[0]) enriched.assessorPersonId = pr.records[0].record_id;
          } catch { /* best effort */ }
        }
      }
      const r = await createRecord<Record<string, unknown>>(TABLES.impactAssessments, toPayload(enriched));
      await get().fetch();
      return (r as any).record_id ?? (r as any).id ?? null;
    } catch (e) { console.error('[Impact] create', e); return null; }
  },

  update: async (id, data) => {
    try {
      await updateRecord(TABLES.impactAssessments, id, toPayload(data));
      await get().fetch();
    } catch (e) { console.error('[Impact] update', e); }
  },

  remove: async (id) => {
    try {
      await deleteRecord(TABLES.impactAssessments, id);
      await get().fetch();
    } catch (e) { console.error('[Impact] remove', e); }
  },

  bulkCreate: async (items) => {
    const ids: number[] = [];
    for (const data of items) {
      try {
        const r = await createRecord<Record<string, unknown>>(TABLES.impactAssessments, toPayload(data));
        const rid = (r as any).record_id ?? (r as any).id;
        if (rid) ids.push(rid);
      } catch (e) { console.error('[Impact] bulkCreate item', e); }
    }
    await get().fetch();
    return ids;
  },

  loadPhotos: async (assessmentId) => {
    try {
      const res = await queryRecords<Record<string, unknown>>(TABLES.impactPhotos, {
        limit: 50,
        filters: [{ field: FP.assessmentId, operator: 'eq', value: String(assessmentId) }],
      });
      return res.records.map(mapPhoto);
    } catch (e) { console.error('[Impact] loadPhotos', e); return []; }
  },

  uploadPhoto: async (assessmentId, base64, index, meta) => {
    try {
      const payload: Record<string, unknown> = {
        [FP.assessmentId]: assessmentId,
        [FP.photoData]: base64,
        [FP.caption]: meta?.caption || `Photo ${index + 1}`,
        [FP.takenBy]: meta?.takenBy || '',
        [FP.takenAt]: new Date().toISOString(),
      };
      await createRecord(TABLES.impactPhotos, payload);
    } catch (e) { console.error('[Impact] uploadPhoto', e); }
  },

  // V1 compat aliases
  loadAssessments: async () => get().fetch(),
  createAssessment: async (data: any) => get().create(data),
  updateAssessment: async (id: any, data: any) => get().update(id, data),
  deleteAssessment: async (id: any) => get().remove(id),
  bulkCreateAssessments: async (items: any[]) => get().bulkCreate(items),
  createSelfReport: async (contactData: any, damageData?: any) => {
    const extra = JSON.stringify({
      phone: contactData.reporterPhone || '',
      livingSituation: damageData?.livingSituation || '',
      insured: damageData?.insured || '',
      floodInsurance: damageData?.floodInsurance || '',
      damageScore: damageData?.damageScore || '',
    });
    return get().create({
      incidentId: contactData.incidentId,
      assessmentType: 'self_report',
      address: contactData.address || '',
      city: contactData.city || '',
      state: contactData.stateCode || contactData.state || '',
      zip: contactData.zip || '',
      assessorName: contactData.reporterName || '',
      assessorEmail: contactData.reporterEmail || '',
      occupancyType: contactData.dwellingType || '',
      homeCount: Number(contactData.occupants) || 1,
      damageCategory: damageData?.damageCategory || '',
      description: damageData?.notes || '',
      status: 'pending',
      notes: extra,
    });
  },
  isLoading: false,
}));
